import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Rocket, Search } from "lucide-react";
import { api } from "../lib/api";
import CategoryCard from "../components/CategoryCard";
import LogoImage from "../components/LogoImage";

const QUICK_CHIPS = [
    { label: "Payment Gateways", slug: "payment-service-providers" },
    { label: "Cloud Hosting", slug: "cloud-computing-platforms" },
    { label: "Marketing Automation", slug: "marketing-automation-software" },
    { label: "AI Chatbots", slug: "generative-ai-chatbots" },
    { label: "Video Platforms", slug: "video-platforms" },
];

const WHY_ITEMS = [
    {
        icon: "💰",
        title: "Pay in Rupees",
        body: "Indian alternatives bill in ₹ and accept UPI, Netbanking and Indian credit cards.",
    },
    {
        icon: "🛡️",
        title: "Data in India",
        body: "Indian data centers for DPDPA 2023 compliance. No more foreign data transfers.",
    },
    {
        icon: "🤝",
        title: "GST Invoicing",
        body: "Get proper GST invoices. Claim as business expenses. Indian vendors only.",
    },
    {
        icon: "⚡",
        title: "IST Support",
        body: "Teams in your timezone. No waiting for US or EU business hours.",
    },
    {
        icon: "🇮🇳",
        title: "Build India's Economy",
        body: "Every subscription funds Indian engineers and Indian jobs.",
    },
    {
        icon: "✅",
        title: "Verified, Not Marketed",
        body: "We only list strictly Indian-owned companies. No subsidiaries. No 'India offices'.",
    },
];

export default function Home() {
    const [categories, setCategories] = useState([]);
    const [recent, setRecent] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [stats, setStats] = useState({
        products: 202,
        categories: 61,
        opportunities: 3,
    });
    const [query, setQuery] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        api.listCategories("count").then(setCategories).catch(() => {});
        api.getRecent(8).then(setRecent).catch(() => {});
        api.getStats().then(setStats).catch(() => {});
        api.getOpportunities().then(setOpportunities).catch(() => {});
    }, []);

    const onSearchSubmit = (e) => {
        e.preventDefault();
        const q = query.trim();
        if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
    };

    return (
        <div data-testid="home-page">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="chakra-bg absolute inset-0 pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-4 lg:px-6 py-16 md:py-24">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-saffron-700 bg-saffron-50 border border-saffron-200 rounded-full px-3 py-1">
                            <span aria-hidden>🇮🇳</span> Made in India
                            Directory
                        </span>
                        <h1 className="mt-5 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-ink">
                            Indian alternatives for{" "}
                            <span className="text-saffron">digital products</span>
                        </h1>
                        <p className="mt-5 text-lg text-ink-muted max-w-2xl leading-relaxed">
                            <strong className="text-ink">
                                {stats.products} verified
                            </strong>{" "}
                            Made-in-India products across{" "}
                            <strong className="text-ink">
                                {stats.categories} categories
                            </strong>{" "}
                            — strictly Indian-owned, no foreign subsidiaries.
                        </p>

                        <form
                            onSubmit={onSearchSubmit}
                            className="mt-7 max-w-2xl flex items-stretch bg-white border border-line rounded-2xl shadow-sm focus-within:ring-2 focus-within:ring-saffron/30 focus-within:border-saffron/40 overflow-hidden"
                            data-testid="hero-search-form"
                        >
                            <Search className="self-center ml-4 h-5 w-5 text-ink-muted" />
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                type="search"
                                placeholder="Search for Razorpay, Zoho, Freshworks, JioSaavn..."
                                className="flex-1 bg-transparent px-4 py-4 outline-none text-base placeholder:text-ink-muted"
                                data-testid="hero-search-input"
                            />
                            <button
                                type="submit"
                                className="btn-saffron px-5 py-3 m-1.5 rounded-xl text-sm font-semibold hidden sm:inline-flex"
                                data-testid="hero-search-submit"
                            >
                                Search
                            </button>
                        </form>

                        <div className="mt-4 flex flex-wrap gap-2">
                            {QUICK_CHIPS.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/category/${c.slug}`}
                                    className="text-sm px-3 py-1.5 rounded-full bg-white border border-line text-ink-muted hover:text-saffron hover:border-saffron/40 transition-colors"
                                    data-testid={`hero-chip-${c.slug}`}
                                >
                                    {c.label}
                                </Link>
                            ))}
                        </div>

                        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
                            {[
                                ["🏢", `${stats.products} Products`],
                                ["📂", `${stats.categories} Categories`],
                                ["✅", "Strictly Indian"],
                                ["🚀", `${stats.opportunities} Opportunities`],
                            ].map(([emoji, label]) => (
                                <div
                                    key={label}
                                    className="bg-white border border-line rounded-xl px-4 py-3 flex items-center gap-2"
                                >
                                    <span aria-hidden>{emoji}</span>
                                    <span className="text-sm font-semibold text-ink">
                                        {label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why BharatStack */}
            <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8">
                <div className="flex items-end justify-between gap-4 mb-6">
                    <div>
                        <h2 className="font-heading font-extrabold text-3xl text-ink">
                            Why <span className="text-saffron">BharatStack</span>
                        </h2>
                        <p className="text-ink-muted text-base mt-1">
                            Six reasons Indian teams switch to local alternatives.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                    {WHY_ITEMS.map((it) => (
                        <div
                            key={it.title}
                            className="bg-white border border-line rounded-2xl p-6 card-lift"
                        >
                            <div className="text-2xl">{it.icon}</div>
                            <h3 className="mt-3 font-heading font-bold text-ink text-lg">
                                {it.title}
                            </h3>
                            <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                                {it.body}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Categories preview */}
            <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-20">
                <div className="flex items-end justify-between gap-4 mb-6">
                    <div>
                        <h2 className="font-heading font-extrabold text-3xl text-ink">
                            Browse by category
                        </h2>
                        <p className="text-ink-muted text-base mt-1">
                            18 of {stats.categories} categories — every one is
                            curated.
                        </p>
                    </div>
                    <Link
                        to="/categories"
                        className="hidden sm:inline-flex items-center text-sm font-semibold text-saffron hover:text-saffron-700"
                        data-testid="see-all-categories-link"
                    >
                        See all {stats.categories} categories
                        <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                    {categories.slice(0, 18).map((c) => (
                        <CategoryCard key={c.slug} category={c} />
                    ))}
                </div>

                <div className="mt-6 sm:hidden">
                    <Link
                        to="/categories"
                        className="inline-flex items-center text-sm font-semibold text-saffron"
                    >
                        See all {stats.categories} categories
                        <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                </div>
            </section>

            {/* Opportunities */}
            {opportunities.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-20" data-testid="home-opportunities">
                    <div className="flex items-end justify-between gap-4 mb-6">
                        <div>
                            <h2 className="font-heading font-extrabold text-3xl text-ink">
                                <span aria-hidden>🚀</span> Open Opportunities
                                — <span className="text-saffron">Build for Bharat</span>
                            </h2>
                            <p className="text-ink-muted text-base mt-1">
                                These categories have no Indian alternative
                                yet. The market is waiting.
                            </p>
                        </div>
                        <Link
                            to="/opportunities"
                            className="hidden sm:inline-flex items-center text-sm font-semibold text-saffron hover:text-saffron-700"
                            data-testid="see-all-opportunities-link"
                        >
                            See all opportunities
                            <ArrowRight className="h-4 w-4 ml-1" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {opportunities.map((opp) => (
                            <Link
                                key={opp.slug}
                                to={`/category/${opp.slug}`}
                                className="bg-gradient-to-br from-saffron-50 to-white border border-saffron/30 rounded-2xl p-6 card-lift block"
                                data-testid={`home-opp-${opp.slug}`}
                            >
                                <div className="text-3xl">
                                    {opp.opportunity?.icon_emoji || "🚀"}
                                </div>
                                <h3 className="mt-3 font-heading font-bold text-ink text-lg">
                                    {opp.name}
                                </h3>
                                <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                                    {opp.opportunity?.homepage_blurb || ""}
                                </p>
                                <span className="mt-4 inline-flex items-center gap-1 bg-indiagreen text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                                    I'm Building This
                                    <ArrowRight className="h-3.5 w-3.5" />
                                </span>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Recently added */}
            <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-20">
                <div className="flex items-end justify-between gap-4 mb-6">
                    <div>
                        <h2 className="font-heading font-extrabold text-3xl text-ink">
                            Recently added
                        </h2>
                        <p className="text-ink-muted text-base mt-1">
                            New Indian alternatives joining the directory.
                        </p>
                    </div>
                    <Link
                        to="/latest"
                        className="hidden sm:inline-flex items-center text-sm font-semibold text-saffron hover:text-saffron-700"
                    >
                        See all recent
                        <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                </div>

                <div className="bg-white border border-line rounded-2xl divide-y divide-line">
                    {recent.map((p) => (
                        <Link
                            key={p.id}
                            to={`/product/${p.slug}`}
                            className="flex items-center gap-4 px-4 sm:px-6 py-3.5 hover:bg-cream transition-colors"
                            data-testid={`recent-${p.slug}`}
                        >
                            <LogoImage
                                src={p.logo_url}
                                domain={p.domain}
                                name={p.name}
                                size={32}
                                rounded="rounded-lg"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-ink truncate">
                                    {p.name}
                                </div>
                                <div className="text-xs text-ink-muted truncate">
                                    {p.category_name}
                                </div>
                            </div>
                            <ArrowRight className="h-4 w-4 text-ink-muted" />
                        </Link>
                    ))}
                </div>
            </section>

            {/* Bottom CTA gradient */}
            <section className="mt-24">
                <div className="bg-gradient-to-r from-saffron via-saffron-600 to-indiagreen text-white">
                    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-16 text-center">
                        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">
                            Know a verified Indian alternative we missed?
                        </h2>
                        <p className="mt-3 text-white/90 text-lg">
                            We only list Indian-founded, Indian-HQ'd companies.
                            No subsidiaries accepted.
                        </p>
                        <Link
                            to="/submit"
                            className="mt-6 inline-flex items-center gap-2 bg-white text-saffron-700 hover:bg-cream font-semibold rounded-xl px-6 py-3 transition-colors"
                            data-testid="cta-submit-product"
                        >
                            Submit a Product
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
